import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, FileCode2, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import { useSiteStyles } from "@/hooks/useSiteStyles";

const API = getApiUrl();

/**
 * Admin -> Integrations -> Planning Applications
 * Powers the "nearby planning applications" sections in the agent
 * dashboard's House Prices tool AND the per-listing Estimate Price modal
 * via PlanIt (planit.org.uk) — free public data, no API key. Both
 * switches here are also duplicated in Admin -> Settings -> My Properties
 * Settings for convenience (same underlying site_styles flags).
 */
export default function PlanningApplicationsIntegrationSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState("NP20 4BD");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [flags, setFlags] = useState({ planningAppsHousePricesEnabled: true, planningAppsEstimateModalEnabled: true });
  const [savingFlag, setSavingFlag] = useState(null);
  const { refetch: refetchSharedSiteStyles } = useSiteStyles();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/integrations/planning-applications-status`, { withCredentials: true }),
      axios.get(`${API}/admin/site-styles`, { withCredentials: true }),
    ])
      .then(([statusRes, stylesRes]) => {
        setStatus(statusRes.data);
        const s = stylesRes.data?.styles || {};
        setFlags({
          planningAppsHousePricesEnabled: s.planningAppsHousePricesEnabled !== false,
          planningAppsEstimateModalEnabled: s.planningAppsEstimateModalEnabled !== false,
        });
      })
      .catch((e) => console.error("Failed to load Planning Applications status:", e))
      .finally(() => setLoading(false));
  }, []);

  const handleFlagToggle = async (key, checked) => {
    setSavingFlag(key);
    try {
      await axios.put(`${API}/admin/site-styles`, { styles: { [key]: checked } }, { withCredentials: true });
      setFlags((prev) => ({ ...prev, [key]: checked }));
      refetchSharedSiteStyles();
      toast.success(checked ? "Enabled" : "Disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    } finally {
      setSavingFlag(null);
    }
  };

  const handleTest = async () => {
    if (!postcode.trim()) {
      toast.error("Enter a UK postcode to test");
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/planning-applications-test-call`,
        { postcode },
        { withCredentials: true }
      );
      setResult(data);
      toast.success(`Found ${data.matches} planning application(s) for ${data.postcode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test lookup failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="planning-apps-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="planning-applications-integration-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileCode2 className="h-4 w-4" />
            PlanIt — Planning Applications
          </CardTitle>
          <CardDescription>
            Free public UK planning applications aggregator (no API key required). Shows
            nearby planning activity — extensions, developments, change of use, etc. —
            alongside AI price estimates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm" data-testid="planning-apps-status">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="font-medium">Connected</span>
            <span className="text-muted-foreground">— no setup needed, always available</span>
          </div>

          <div className="text-xs text-muted-foreground">
            <p>Used by: {status?.used_by?.join(" · ")}</p>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="font-normal text-sm">Show on House Prices tool</Label>
                <p className="text-xs text-muted-foreground">Agent dashboard's postcode-only House Prices section</p>
              </div>
              {savingFlag === "planningAppsHousePricesEnabled" ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Switch
                  checked={flags.planningAppsHousePricesEnabled !== false}
                  onCheckedChange={(checked) => handleFlagToggle("planningAppsHousePricesEnabled", checked)}
                  data-testid="feature-planning-apps-house-prices-switch"
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-border pt-3">
              <div>
                <Label className="font-normal text-sm">Show on Estimate Price modal</Label>
                <p className="text-xs text-muted-foreground">Per-listing "Estimate Price" CTA (agent + master admin)</p>
              </div>
              {savingFlag === "planningAppsEstimateModalEnabled" ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Switch
                  checked={flags.planningAppsEstimateModalEnabled !== false}
                  onCheckedChange={(checked) => handleFlagToggle("planningAppsEstimateModalEnabled", checked)}
                  data-testid="feature-planning-apps-estimate-modal-switch"
                />
              )}
            </div>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Label htmlFor="planning-apps-test-postcode" className="text-sm">Test lookup by postcode</Label>
            <div className="flex items-center gap-2">
              <Input
                id="planning-apps-test-postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. NP20 4BD"
                className="max-w-[200px]"
                data-testid="planning-apps-test-postcode-input"
              />
              <Button onClick={handleTest} disabled={testing} size="sm" data-testid="planning-apps-test-call-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Test Lookup
              </Button>
            </div>
          </div>

          {result && (
            <div className="text-sm rounded-md p-3 border border-green-200 bg-green-50 text-green-800" data-testid="planning-apps-test-result">
              <p className="font-medium mb-1">{result.matches} planning application(s) found near {result.postcode}</p>
              {result.applications.slice(0, 5).map((a, i) => (
                <p key={i} className="text-xs">
                  {a.date} — {a.address} — <span className="font-medium">{a.status}</span>
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationApiLogToggle provider="planning_applications" />
    </div>
  );
}
